import { useEffect, useId, useRef } from 'react';

import isEqual from 'lodash-es/isEqual';
import { CircleHelpIcon, InfoIcon, PlusIcon } from 'lucide-react';
import { Path, useFieldArray, useForm, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { move } from '@/components/organisms/Toolkit/helper';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBrainId } from '@/hooks/useBrainId';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import { defaultQuestionOptions, newQuestion } from '@/redux/dialogs/helper';
import { selectNodeIdByActionId, selectQuestionSetAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Question, QuestionType } from '@/shared/types/action';
import { flattenFormErrors } from '@/util/forms';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { QUESTION_SET_LIMITS } from '@/util/validation/constants';
import {
  clearStaleConditions,
  questionSetSchema,
  QuestionSetForm,
  renameOptionValueReferences,
  renameQuestionReferences,
  withoutOptionValueReferences,
  withoutQuestionReferences,
} from '@/util/validation/schemas/question-set-schemas';

import { QuestionFields } from './QuestionFields';
import { toAction, toForm, withChoiceDefaults } from './questionSetForm';
import ToolkitWrapper from '../../ToolkitWrapper';

/** Mirrors the form errors into the dialog alerts; a leaf, so only it re-renders per keystroke. */
const QuestionSetAlerts = ({ actionId, nodeId }: { actionId: string; nodeId: string }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<QuestionSetForm>();
  const { errors } = useFormState({ control });
  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId,
    title: t('actions.types.question_set'),
    type: 'question_set',
  });
  const errorsKey = JSON.stringify(flattenFormErrors(errors));
  const previous = useRef<Record<string, string>>({});

  useEffect(() => {
    const current: Record<string, string> = JSON.parse(errorsKey);
    for (const path of new Set([...Object.keys(previous.current), ...Object.keys(current)])) {
      if (previous.current[path] !== current[path]) {
        setFieldError(path, current[path]);
      }
    }
    previous.current = current;
  }, [errorsKey, setFieldError]);

  return null;
};

const ToolkitActionQuestionSet = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const brainId = useBrainId();
  const { dialogId } = useParams<{ dialogId: string }>();
  const { detailedOptions } = useDialogs(brainId, dialogId);

  const action = useSelector(selectQuestionSetAction);
  const actionId = action?.action_id ?? '';
  const nodeId = useSelector(selectNodeIdByActionId(actionId)) ?? '';

  const form = useForm<QuestionSetForm>({
    mode: 'onTouched',
    resolver: createYupResolver<QuestionSetForm>(questionSetSchema),
    defaultValues: toForm(action),
  });
  const { control, getValues, setValue, trigger } = form;
  const { fields, replace } = useFieldArray({ control, name: 'questions' });
  const questionsLabelId = useId();

  const hasErrors = useSelector(selectHasError(actionId));
  useEffect(() => {
    trigger();
  }, [hasErrors, trigger]);

  if (!action) {
    return null;
  }

  // Every edit path ends here. A structural edit is one `replace`, so a stale `show_if`
  // never survives a reorder, a delete or a type change.
  const commitQuestions = (next: Question[]) => {
    const cleaned = clearStaleConditions(next);
    if (!isEqual(cleaned, getValues('questions'))) {
      replace(cleaned);
    }
    // A uniqueness error lands on the later duplicate, which `onTouched` never revalidates.
    trigger('questions');
    const saved = toAction({ ...getValues(), questions: cleaned });
    if (
      !isEqual(saved.question_set, action.question_set) ||
      saved.trigger_node_id !== action.trigger_node_id
    ) {
      dispatch(updateAction({ actionId, action: saved }));
    }
  };

  const commit = () => commitQuestions(getValues('questions'));

  const handleAddQuestion = () => {
    const current = getValues('questions');
    commitQuestions([...current, newQuestion(current.map(({ key }) => key))]);
  };

  const handleRemoveQuestion = (index: number) => {
    const current = getValues('questions');
    const rest = current.filter((_, i) => i !== index);
    commitQuestions(withoutQuestionReferences(rest, current[index].key));
  };

  const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
    const current = getValues('questions');
    const question = current[questionIndex];
    const value = question.options?.[optionIndex]?.value ?? '';
    const next = current.map((item, i) =>
      i === questionIndex
        ? { ...item, options: item.options?.filter((_, o) => o !== optionIndex) }
        : item
    );
    commitQuestions(withoutOptionValueReferences(next, question.key, value));
  };

  // Runs on blur, so only the changed conditions are set: a `replace` would remount the row
  // that takes the focus next.
  const setChangedConditions = (rename: (current: Question[]) => Question[]) => {
    const current = getValues('questions');
    const changed: Path<QuestionSetForm>[] = [];
    rename(current).forEach((question, i) => {
      if (question.show_if !== current[i].show_if) {
        setValue(`questions.${i}.show_if`, question.show_if);
        changed.push(`questions.${i}.show_if`);
      }
      question.options?.forEach((option, o) => {
        if (option.show_if !== current[i].options?.[o]?.show_if) {
          setValue(`questions.${i}.options.${o}.show_if`, option.show_if);
          changed.push(`questions.${i}.options.${o}.show_if`);
        }
      });
    });
    if (changed.length > 0) {
      trigger(changed);
    }
  };

  const handleRenameOptionValue = (questionIndex: number, from: string, to: string) =>
    setChangedConditions((current) =>
      renameOptionValueReferences(current, current[questionIndex].key, from, to)
    );

  const handleRenameKey = (from: string, to: string) =>
    setChangedConditions((current) => renameQuestionReferences(current, from, to));

  const handleClearTriggerNode = () => {
    setValue('trigger_node_id', '');
    commit();
  };

  const handleMoveQuestion = (index: number, to: number) =>
    commitQuestions(move(getValues('questions'), index, to));

  const handleTypeChange = (index: number, type: QuestionType) =>
    commitQuestions(
      getValues('questions').map((question, i) => {
        if (i !== index) {
          return question;
        }
        if (type === 'text') {
          return { ...question, type, options: undefined, allow_other: undefined };
        }
        return withChoiceDefaults({
          ...question,
          type,
          options: question.options?.length ? question.options : defaultQuestionOptions(),
        });
      })
    );

  const nodeOptions = detailedOptions.map((option) => ({
    value: option.value,
    label: option.label,
    category: option.dialog_name,
    disabled: option.value === nodeId,
  }));

  return (
    <ToolkitWrapper type="question_set">
      <Form {...form}>
        <QuestionSetAlerts actionId={actionId} nodeId={nodeId} />
        {/* Text inputs commit on blur through bubbling; the Radix controls commit on change. */}
        <div className="flex flex-col gap-4 pb-6" onBlur={commit}>
          <Alert variant="info" className="text-foreground-muted">
            <InfoIcon size={16} />
            <AlertDescription>{t('dialog.question_set.banner')}</AlertDescription>
          </Alert>

          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.question_set.title')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    maxLength={QUESTION_SET_LIMITS.TITLE}
                    placeholder={t('dialog.question_set.default_title')}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t('dialog.question_set.title_description')}</FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="questions"
            render={() => (
              <FormItem>
                <FormLabel id={questionsLabelId}>
                  {t('dialog.question_set.questions', {
                    0: fields.length,
                    1: QUESTION_SET_LIMITS.QUESTIONS,
                  })}
                </FormLabel>
                <FormControl>
                  <div
                    role="group"
                    aria-labelledby={questionsLabelId}
                    className="flex flex-col gap-3"
                  >
                    {fields.map((field, index) => (
                      <QuestionFields
                        key={field.id}
                        index={index}
                        count={fields.length}
                        onCommit={commit}
                        onRemove={handleRemoveQuestion}
                        onRemoveOption={handleRemoveOption}
                        onRenameOptionValue={handleRenameOptionValue}
                        onRenameKey={handleRenameKey}
                        onMove={handleMoveQuestion}
                        onTypeChange={handleTypeChange}
                      />
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
                <Button
                  type="button"
                  variant="outline-neutral"
                  size="sm"
                  disabled={fields.length >= QUESTION_SET_LIMITS.QUESTIONS}
                  onClick={handleAddQuestion}
                >
                  <PlusIcon size={14} />
                  {t('dialog.question_set.add_question')}
                </Button>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="trigger_node_id"
            render={() => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.question_set.trigger_node')}
                  <InfoTooltip content={t('dialog.question_set.trigger_node_help')} size="large">
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <FormControl>
                  <FormAutocomplete
                    control={control}
                    options={nodeOptions}
                    name="trigger_node_id"
                    ariaLabel={t('dialog.question_set.trigger_node')}
                    placeholder={t('dialog.question_set.trigger_node_placeholder')}
                    onChange={commit}
                    onClear={handleClearTriggerNode}
                  />
                </FormControl>
                <FormDescription>
                  {t('dialog.question_set.trigger_node_description')}
                </FormDescription>
              </FormItem>
            )}
          />
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionQuestionSet;
